import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const VARIANT_STYLES = {
  success:
    'border-emerald-200 bg-white text-slate-800 ring-emerald-900/5 [&_p]:text-slate-800',
  error: 'border-red-200 bg-white text-slate-800 ring-red-900/5 [&_p]:text-red-900',
};

export function useToast() {
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, variant = 'success') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => {
    setToast(null);
  }, []);

  return { toast, showToast, dismissToast };
}

export function ToastContainer({ toast, onDismiss }) {
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => onDismiss(), 5000);
    return () => window.clearTimeout(id);
  }, [toast, onDismiss]);

  useEffect(() => {
    if (!toast) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toast, onDismiss]);

  if (!toast || typeof document === 'undefined') return null;

  const { message, variant } = toast;
  const panelClass = VARIANT_STYLES[variant] ?? VARIANT_STYLES.success;

  return createPortal(
    <div className="pointer-events-none fixed right-4 top-20 z-[200] sm:right-6 sm:top-24">
      <div
        role={variant === 'error' ? 'alert' : 'status'}
        aria-live={variant === 'error' ? 'assertive' : 'polite'}
        className={`pointer-events-auto flex max-w-md items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg shadow-slate-900/15 ring-1 ${panelClass}`}
      >
        <p className="min-w-0 flex-1 pt-0.5 leading-snug">{message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
          aria-label="Dismiss notification"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
    </div>,
    document.body
  );
}
